import React from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_SCAFFOLD,
  GET_ALL_SCAFFOLDS,
  LINK_SCAFFOLD_JOBS,
  REMOVE_SCAFFOLD,
  UPDATE_SCAFFOLD,
} from '../../../graph-queries/tar/scaffold';
import { TScaffold } from '../../../lib/types';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import ScaffoldForm from '../../organisms/ScaffoldForm/ScaffoldForm';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import { ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import { SCAFFOLD_STATUS_ENUM } from '../../../lib/constants/dbConstants';

export const columns: ColumnDef<TScaffold>[] = [
  {
    header: 'Plan Ref',
    accessorKey: 'planRef',
    enableSorting: false,
  },
  {
    header: 'Description',
    accessorKey: 'description',
    enableSorting: false,
  },
  {
    header: 'Weight of Scaffold (kg)',
    accessorKey: 'weight',
    enableSorting: false,
  },
  {
    header: 'Safe Working Limit (kg)',
    accessorKey: 'safeWorkingLimit',
    enableSorting: false,
  },
  {
    header: 'Width (m)',
    accessorKey: 'width',
    enableSorting: false,
  },
  {
    header: 'Height (m)',
    accessorKey: 'height',
    enableSorting: false,
  },
  {
    header: 'Length (m)',
    accessorKey: 'length',
    enableSorting: false,
  },
  {
    header: 'Team Size',
    accessorKey: 'teamSize',
    enableSorting: false,
  },
  {
    header: 'Erect',
    accessorKey: 'erectHours',
    enableSorting: false,
  },
  {
    header: 'Dismantle',
    accessorKey: 'dismantleHours',
    enableSorting: false,
  },
  {
    header: 'Overside Cover',
    accessorKey: 'outsideCover',
    accessorFn: (row) => (row.outsideCover === true ? 'Yes' : 'No'),
    enableSorting: false,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    enableSorting: false,
    accessorFn: (row) => SCAFFOLD_STATUS_ENUM[row.status],
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const ScaffoldsPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const { data, loading } = useQuery(GET_ALL_SCAFFOLDS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const [createScaffold, { loading: isCreating }] = useMutation(CREATE_SCAFFOLD, {
    refetchQueries: [GET_ALL_SCAFFOLDS],
    awaitRefetchQueries: true,
  });
  const [updateScaffold, { loading: isUpdating }] = useMutation(UPDATE_SCAFFOLD, {
    refetchQueries: [GET_ALL_SCAFFOLDS],
    awaitRefetchQueries: true,
  });
  const [linkScaffoldJobs, { loading: isLinking }] = useMutation(LINK_SCAFFOLD_JOBS, {
    refetchQueries: [GET_ALL_SCAFFOLDS],
    awaitRefetchQueries: true,
  });
  const [removeScaffold, { loading: isRemoving }] = useMutation(REMOVE_SCAFFOLD, {
    refetchQueries: [GET_ALL_SCAFFOLDS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (scaffold: TScaffold) => {
    const { scaffoldId, updatedBy, updatedAt, tarJobs, ...input } = scaffold;
    return createScaffold({ variables: { input } }).then(() =>
      setAlertText('Scaffold successfully added')
    );
  };
  const onEditSubmit = (scaffold: TScaffold) => {
    const { updatedBy, updatedAt, tarJobs, ...input } = scaffold;
    return updateScaffold({ variables: { input } }).then(() =>
      setAlertText('Scaffold successfully updated')
    );
  };

  const onLinkSubmit = (selection: TScaffold[], jobIds: number[]) => {
    return Promise.all(
      selection.map((scaffold) =>
        linkScaffoldJobs({
          variables: {
            scaffoldId: scaffold.scaffoldId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Scaffold successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TScaffold[]) => {
    return Promise.all(
      selection.map((scaffold) =>
        removeScaffold({ variables: { scaffoldId: scaffold.scaffoldId } })
      )
    ).then(() => setAlertText(`Scaffold successfully deleted`));
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;
  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TScaffold>
            hideSelectionColumn
            aspectName="scaffold"
            data={(data?.allScaffolds as unknown as TScaffold[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <ScaffoldForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <ScaffoldForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the scaffold tracker..."
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default ScaffoldsPage;
